import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";
import { Inbox, Phone, Mail, Trash2 } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/date";

const STATUSES = [
  { value: "new", label: "New", color: "#FF6B00" },
  { value: "contacted", label: "Contacted", color: "#3B82F6" },
  { value: "follow_up", label: "Follow-up", color: "#F59E0B" },
  { value: "converted", label: "Converted", color: "#22C55E" },
  { value: "closed", label: "Closed", color: "#71717A" },
];

export default function Enquiries() {
  usePageTitle("Enquiries");
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => {
    const params = statusFilter ? { status: statusFilter } : {};
    return api.get("/enquiries", { params }).then((r) => setRows(r.data));
  };

  useEffect(() => { load();  }, [statusFilter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e) { alert(formatApiError(e)); }
  };

  const addNote = async (id, note) => {
    if (!note?.trim()) return;
    try {
      await api.patch(`/enquiries/${id}`, { note });
      const { data } = await api.get("/enquiries");
      setRows(data);
      setSelected(data.find((r) => r.id === id) || null);
    } catch (e) { alert(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try { await api.delete(`/enquiries/${id}`); load(); setSelected(null); }
    catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="enquiries-root">
      <div className="border-b border-[hsl(var(--border))] pb-6 flex items-end justify-between">
        <div>
          <div className="overline mb-2">Website Inbox</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Enquiries</h1>
        </div>
        <Inbox size={40} className="text-[#FF6B00]" />
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setStatusFilter("")}
          className={`btn-swiss ${!statusFilter ? "btn-accent" : "btn-outline"}`}
          data-testid="enquiry-filter-all"
        >All ({rows.length})</button>
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`btn-swiss ${statusFilter === s.value ? "btn-accent" : "btn-outline"}`}
            data-testid={`enquiry-filter-${s.value}`}
          >{s.label}</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 card-swiss max-h-[70vh] overflow-y-auto">
          {rows.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">No enquiries found.</div>
          )}
          {rows.map((e) => {
            const meta = STATUSES.find((s) => s.value === e.status) || STATUSES[0];
            return (
              <button
                key={e.id}
                onClick={() => setSelected(e)}
                className={`w-full text-left p-4 border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] ${selected?.id === e.id ? "bg-[hsl(var(--muted))]" : ""}`}
                data-testid={`enquiry-item-${e.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{e.name}</div>
                  <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 border" style={{ borderColor: meta.color, color: meta.color }}>
                    {meta.label}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground font-mono mt-1">{e.mobile}</div>
                <div className="text-xs mt-1 line-clamp-2 text-zinc-600">{e.requirement || e.message}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{fmtDateTime(e.created_at)}</div>
              </button>
            );
          })}
        </div>

        <div className="lg:col-span-3">
          {selected ? (
            <div className="card-swiss p-6 space-y-4" data-testid="enquiry-detail">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-2xl font-black">{selected.name}</div>
                  <div className="text-sm text-muted-foreground">{selected.business || "—"}</div>
                </div>
                <button onClick={() => remove(selected.id)} className="p-1.5 hover:text-[#FF6B00]" data-testid="delete-enquiry">
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="flex flex-wrap gap-3 text-sm">
                <a href={`tel:${selected.mobile}`} className="flex items-center gap-2 hover:text-[#FF6B00]"><Phone size={14} /> {selected.mobile}</a>
                {selected.email && <a href={`mailto:${selected.email}`} className="flex items-center gap-2 hover:text-[#FF6B00]"><Mail size={14} /> {selected.email}</a>}
                <a href={`https://wa.me/91${selected.mobile.replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Hi ${selected.name}, this is Brothers Advertising responding to your enquiry.`)}`} target="_blank" rel="noreferrer" className="btn-swiss btn-outline" style={{ borderColor: "#25D366", color: "#25D366" }}>WhatsApp</a>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm border-t border-[hsl(var(--border))] pt-4">
                {[
                  ["Requirement", selected.requirement || "—"],
                  ["Preferred Time", selected.preferred_contact_time || "Any time"],
                  ["Source", selected.source || "website"],
                  ["Received", fmtDateTime(selected.created_at)],
                ].map(([k, v]) => (
                  <div key={k}><div className="overline">{k}</div><div className="mt-1">{v}</div></div>
                ))}
              </div>
              {selected.message && (
                <div className="border-t border-[hsl(var(--border))] pt-4">
                  <div className="overline mb-2">Message</div>
                  <div className="text-sm whitespace-pre-wrap">{selected.message}</div>
                </div>
              )}
              <div className="border-t border-[hsl(var(--border))] pt-4">
                <div className="overline mb-2">Update Status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => setStatus(selected.id, s.value)}
                      disabled={selected.status === s.value}
                      className="btn-swiss btn-outline disabled:opacity-40"
                      data-testid={`set-status-${s.value}`}
                    >{s.label}</button>
                  ))}
                </div>
              </div>
              <div className="border-t border-[hsl(var(--border))] pt-4">
                <div className="overline mb-2">Notes / Follow-up log</div>
                <form
                  onSubmit={(ev) => { ev.preventDefault(); const t = ev.target.elements.n.value; addNote(selected.id, t); ev.target.reset(); }}
                  className="flex gap-2 mb-3"
                >
                  <input name="n" placeholder="Add a note…" className="flex-1 border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]" data-testid="enquiry-note-input" />
                  <button className="btn-swiss btn-accent" data-testid="enquiry-note-add">Add</button>
                </form>
                <div className="space-y-2">
                  {(selected.notes || []).slice().reverse().map((n, i) => (
                    <div key={i} className="text-sm border-l-2 border-[#FF6B00] pl-3 py-1">
                      <div>{n.text}</div>
                      <div className="text-xs text-muted-foreground">{n.by} · {fmtDateTime(n.at)}</div>
                    </div>
                  ))}
                  {(!selected.notes || selected.notes.length === 0) && (
                    <div className="text-sm text-muted-foreground">No notes yet.</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="card-swiss p-12 text-center text-muted-foreground text-sm">
              Select an enquiry to view details.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
